import React, { FC, memo } from 'react'
import classNames from 'classnames'
import camelCase from 'lodash/camelCase'
import moment from 'moment'
import EditIcon from 'transfix-icons/build/EditIcon'
import { Button } from 'transfix-ui/components/Button'
import { Card, ICardProps } from 'transfix-ui/components/Card'
import { IconButton } from 'transfix-ui/components/IconButton'
import { Spinner } from 'transfix-ui/components/Spinner'
import { makeStyles, Typography } from 'transfix-ui/core'

interface IShipperCardProps {
  title: string
  editable?: boolean
  onEdit?: () => void
  lastUpdated?: string
  CardProps?: Partial<Omit<ICardProps, 'title' | 'editable' | 'onEdit'>>
  loading?: boolean
  sync?: boolean
  onSync?: () => void
  syncDisabled?: boolean
}

const useShipperCardStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(1.5, 3),
  },
  cardInner: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  title: {
    padding: '6px 0',
  },
  spaceRight: {
    marginRight: theme.spacing(1),
  },
}))

const ShipperCard: FC<IShipperCardProps> = memo(
  ({
    sync,
    children,
    editable = false,
    lastUpdated: lastUpdateProp,
    onEdit,
    onSync,
    title,
    CardProps = {},
    loading = false,
    syncDisabled = false,
  }) => {
    const classes = useShipperCardStyles()
    const { className: cardClassNameProp, dataTest, ...otherCardProps } = CardProps
    const cardClassName = classNames(classes.root, cardClassNameProp)
    const lastUpdated = lastUpdateProp && moment(lastUpdateProp).format('M/YYYY')
    const cardDataTestId = dataTest || `shipper-${camelCase(title)}-card`

    return (
      <Card className={cardClassName} {...otherCardProps} dataTest={cardDataTestId}>
        <div className={classes.cardInner}>
          <div className={classes.header}>
            {title && (
              <Typography variant='h3' className={classes.title}>
                {title}
              </Typography>
            )}
            {editable && !loading && (
              <div>
                {!syncDisabled && sync && onSync && (
                  <Button className={classes.spaceRight} onClick={onSync} color='secondary'>
                    NetSuite Sync
                  </Button>
                )}
                {lastUpdated && (
                  <Typography
                    className={classes.spaceRight}
                    variant='caption'
                    color='textSecondary'>
                    Last Updated {lastUpdated}
                  </Typography>
                )}
                <IconButton
                  Icon={EditIcon}
                  onClick={onEdit}
                  data-test={`${cardDataTestId}-edit-button`}
                  color='secondary'
                  label='Edit'
                  size='large'
                />
              </div>
            )}
          </div>
          {loading && <Spinner position='absolute' />}
          {!loading && <div>{children}</div>}
        </div>
      </Card>
    )
  }
)

export default ShipperCard
